import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useEffect } from 'react';
import useSWR from 'swr';

import { usePageContext } from '@/features/page/context';
import { Page } from '@/features/page/types';
import usePublicTranslation from '@/hooks/usePublicTranslation';
import useReadingTimeCalculator from '@/hooks/useReadingTimeCalculator';
import { formatUnixTimestamp } from '@/utils/date';
import { getExcerptWithoutHtml } from '@/utils/page';

type Props = {
  sectionTitle?: string;
  showCreationDate?: boolean;
  showReadingTime?: boolean;
} & Record<string, unknown>;

const PageReadNext: React.FC<Props> = ({ sectionTitle, showCreationDate = true, showReadingTime = true, ...props }) => {
  const t = usePublicTranslation();
  const { alias } = usePageContext();
  const router = useRouter();

  const { data, isLoading } = useSWR<Page>(alias ? `/content/public/page/next-by-full-path?path=${alias}` : null);

  const { readingTime } = useReadingTimeCalculator({
    content: data?.rawBody || ''
  });

  useEffect(() => {
    // preload the next page
    if (data?.alias) {
      router.prefetch(data.alias);
    }
  }, [data, router]);

  if (!data) return null;

  return (
    <div {...props}>
      <div className="read-next-head">{sectionTitle || t('page.read_next')}</div>
      <div className="read-next-body">
        <figure className="read-next-figure">
          {
            // eslint-disable-next-line @next/next/no-img-element
            !isLoading && data.meta?.featuredImage && <img src={data.meta.featuredImage} alt="" />
          }
        </figure>
        <div className="read-next-content">
          <div className="read-next-title">
            <Link href={data.alias}>{data.title}</Link>
          </div>
          <div className="read-next-excerpt">
            <p dangerouslySetInnerHTML={{ __html: getExcerptWithoutHtml(data.rawBody, 180) }} />
            <Link href={data.alias} className="read-next-read-more">
              {t('page.read_more')}
            </Link>
          </div>
          {(showCreationDate || showReadingTime) && (
            <div className="read-next-meta">
              {showCreationDate && (
                <>
                  <span className="read-next-meta-date">{formatUnixTimestamp(data.createdOn, 'MMMM DD, YYYY')}</span>
                  {showReadingTime && <span className="read-next-meta-separator">/</span>}
                </>
              )}
              {showReadingTime && (
                <span className="read-next-meta-reading-time">
                  {t('page.reading_time', { time: readingTime || t('general.na') })}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageReadNext;
